// Full-screen detail sheet: the warm background, one scrolling glass card and
// the shared close button. The supplement, procedure and medication fact
// sheets each hand-build this shell; new detail sheets use this one instead.
//
// Closing slides the card down and fades it before `onClose` runs, unless the
// patient prefers reduced motion. The caller provides the content.
import { useCallback, useEffect, type ReactNode } from "react";
import { Modal, View } from "react-native";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useScrollHaptics } from "../hooks/useScrollHaptics";
import { ToastHost } from "../hooks/useToast";
import { SCREEN_PADDING_TOP, springs } from "../theme/tokens";
import GlassScreenLayout from "./GlassScreenLayout";
import { CloseButton } from "./CloseButton";
import { cardSurface } from "./CardSurface";

type Props = {
  visible: boolean;
  onClose: () => void;
  /** Accessible name of the close button, translated by the caller. */
  closeLabel: string;
  testID?: string;
  children: ReactNode;
};

export function DetailSheet({
  visible,
  onClose,
  closeLabel,
  testID,
  children,
}: Props) {
  const reducedMotion = useReducedMotion();
  const onScroll = useScrollHaptics();
  const translateY = useSharedValue(0);
  const opacity = useSharedValue(1);

  useEffect(() => {
    if (!visible) return;
    translateY.value = 0;
    opacity.value = 1;
  }, [opacity, translateY, visible]);

  const handleClose = useCallback(() => {
    if (reducedMotion) {
      onClose();
      return;
    }
    opacity.value = withTiming(0, { duration: 200 });
    translateY.value = withSpring(80, springs.sheet, (finished) => {
      if (finished) runOnJS(onClose)();
    });
  }, [onClose, opacity, reducedMotion, translateY]);

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={handleClose}
    >
      <GlassScreenLayout>
        <Animated.View style={animatedStyle}>
          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={onScroll}
            scrollEventThrottle={16}
          >
            <View
              testID={testID}
              style={{
                ...cardSurface(),
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              <View
                style={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
              >
                <CloseButton
                  onPress={handleClose}
                  action="close_detail_sheet"
                  accessibilityLabel={closeLabel}
                />
              </View>
              {children}
            </View>
          </Animated.ScrollView>
        </Animated.View>
      </GlassScreenLayout>
      {visible ? <ToastHost /> : null}
    </Modal>
  );
}
